/**
 * ============================================
 * MULTIPLAYER (ONLINE PVP) DASHBOARD
 * ============================================
 *
 * Built from the snapshot the analytics worker writes once a day: the union
 * of S3 per-match files and DynamoDB online_pvp_data, the main-menu
 * "Online PVP" presses, and (separately) the PVP level rows.
 *
 * Layout follows the Overseer Multiplayer Analytics spec:
 *   Filters (Platform, Date range, Level/Map) -> highlight bar ->
 *   PVP levels -> Matches & matchmaking -> Network -> Geography & platforms,
 *   then every other graph under "More detail". Graph ids match
 *   MULTIPLAYER_TAB_PLAN.md section 8.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, CalendarDays, Database, Globe, RefreshCw, RotateCw, Swords } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Cell,
  LabelList,
} from 'recharts';
import { useDateRange } from '@/context/DateRangeContext';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { CountryMultiSelect } from '@/components/common/CountryMultiSelect';
import { CountryChips } from '@/components/common/CountryChips';
import { CountryCard, CountryScope, useCardOverrides } from '@/components/common/CardCountryFilter';
import { UNKNOWN_COUNTRY, countryName, type CountryOption } from '@/utils/countryRegions';
import { PlayersByCountryCard } from './PlayersByCountryCard';
import { TimeScaleToggle, type TimeScale } from '@/components/common/TimeScaleToggle';
import { useMultiplayerAnalytics, usePvpLevels, type MpReport, type MpSnapshot, type MpStatus } from '@/hooks/useMultiplayerAnalytics';
import {
  ALL,
  ONLINE_MODES,
  addDays,
  PVP_MODE_LABELS,
  PVP_MODE_ORDER,
  daysBetween,
  decodeEntry,
  decodeReports,
  distinct,
  entryByPeriod,
  filterEntry,
  filterReports,
  firstVersionByDevice,
  groupByPeriod,
  groupMatches,
  histogram,
  median,
  pct,
  percentile,
  playedDates,
  round1,
  regionLabel,
  withInviteEntries,
  type EntryRow,
  type InRange,
  type MpFilters,
} from './mpData';
import {
  ChartCard,
  ChartWindowContext,
  Donut,
  EmptyChart,
  HistogramChart,
  SectionTitle,
  StatTile,
  ThemedCard,
  athNowLines,
  periodAxis,
  useChartTheme,
  END_REASON_COLORS,
  END_REASON_LABELS,
  INPUT_LABELS,
  MATCH_TYPE_LABELS,
  PALETTE,
  SOURCE_COLORS,
  SOURCE_LABELS,
  type ChartWindow,
} from './MpCharts';
import { MatchExplorer, type MatchRow } from './MatchExplorer';
import { PvpLevelsSection } from './PvpLevelsSection';
import { MARKER_CHART_MARGIN } from '@/components/common/ChartMarkers';

// Outside the current layout spec. Flip to bring them back.
const SHOW_VERSION_REGION_FILTERS = false;
const SHOW_MATCH_EXPLORER = false;

type ScaleKey = 'g1' | 'g2' | 'g7' | 'g19' | 'g21' | 'g26' | 'g31';

const STEP_LABELS: Record<string, string> = {
  opened_no_mode: 'Never picked Host / Join / Matchmaking',
  mode_no_room: 'Picked a mode, never reached a room',
  room_no_opponent: 'In a room, no opponent came',
  opponent_not_ready: 'Opponent came, not both ready',
  ready_not_started: 'Both ready, match never started',
  played: 'Played a match',
};
const STEP_COLORS = ['#94a3b8', '#f59e0b', '#ef4444', '#a855f7', '#6366f1', '#10b981'];
const END_KEYS = ['portal', 'menu', 'opponent', 'disconnected'] as const;

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const fmtInt = (n: number) => Math.round(n).toLocaleString();
const isDisconnect = (r: MpReport) => r.er === 3 || r.disc > 0;

function formatWhen(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const STATE_TEXT: Record<string, string> = {
  never_run: 'Never run',
  running: 'Running',
  succeeded: 'Succeeded',
  kept_previous: 'Stopped — previous data kept',
  failed: 'Failed',
  skipped: 'Skipped',
  unknown: 'Unknown',
};


/** Bounds of a chart window. 'range' follows the date range; Day / Week / Month end at the
 *  last complete day and, like All Time, ignore the date range. */
function windowBounds(win: ChartWindow, dateInRange: InRange, dateStart: string | null | undefined, dateEnd: string | null | undefined, dataThrough: string) {
  if (win === 'range') return { inRange: dateInRange as InRange | null, rangeStart: dateStart || null, rangeEnd: dateEnd || null, label: '' };
  if (win === 'all' || !dataThrough) return { inRange: null, rangeStart: null, rangeEnd: null, label: 'All time' };
  const from = addDays(dataThrough, win === 'day' ? 0 : win === 'week' ? -6 : -29);
  const within: InRange = date => !!date && date >= from && date <= dataThrough;
  return { inRange: within as InRange | null, rangeStart: from, rangeEnd: dataThrough, label: from === dataThrough ? from : `${from} – ${dataThrough}` };
}

/** Everything the charts derive, for one window and one set of filters (incl. countries). */
function computeWindowData(win: ChartWindow, allReports: MpReport[], allEntry: EntryRow[],
  filters: MpFilters, scales: Record<ScaleKey, TimeScale>, dataThrough: string, snapshot: MpSnapshot | null,
  dateInRange: InRange, dateStart: string | null | undefined, dateEnd: string | null | undefined) {
  const bounds = (() => windowBounds(win, dateInRange, dateStart, dateEnd, dataThrough))();
  const { inRange, rangeStart, rangeEnd } = bounds;
  const srcReports = allReports;
  const srcEntry = allEntry;

  const reports = (() => filterReports(srcReports, inRange, filters))();
  const reportsAnyDate = (() => filterReports(srcReports, null, filters))();
  const entry = (() => filterEntry(srcEntry, inRange, filters))();
  const matchList = ((() => Array.from(groupMatches(reports).entries()).map(([sid, rs]) => ({
      sid, reports: rs, date: rs.map(r => r.date).sort()[0],
    }))) as () => MatchRow[])();
  const completeMatches = (() => matchList
      .map(m => ({ ...m, host: m.reports.find(r => r.slot === 0), guest: m.reports.find(r => r.slot === 1) }))
      .filter((m): m is MatchRow & { host: MpReport; guest: MpReport } => !!m.host && !!m.guest))();

  // ---- overview + highlight bar ------------------------------------------------
  const overview = (() => {
    const opened = new Set(entry.map(e => e.dev));
    const played = new Set(reports.filter(r => r.dev >= 0).map(r => r.dev));
    let openedAndPlayed = 0;
    opened.forEach(d => { if (played.has(d)) openedAndPlayed++; });
    const inviteOnly = new Set(entry.filter(e => e.invite).map(e => e.dev)).size;

    const dates = reports.map(r => r.date).sort();
    const from = rangeStart || dates[0];
    const lastData = dates[dates.length - 1];
    const toCandidate = rangeEnd || lastData;
    const to = toCandidate && dataThrough && toCandidate > dataThrough ? dataThrough : toCandidate;
    const days = from && to && to >= from ? daysBetween(from, to) : 0;

    const withSeries = matchList.filter(m => m.reports.some(r => r.sn > 0));
    const spiky = withSeries.filter(m => m.reports.some(r => r.speak > 300)).length;
    const bySource = [0, 0, 0];
    reports.forEach(r => { bySource[r.src] = (bySource[r.src] || 0) + 1; });
    const lengths = matchList.map(m => Math.max(...m.reports.map(r => r.el)));

    return {
      opened: opened.size,
      played: played.size,
      conversion: pct(openedAndPlayed, opened.size),
      inviteOnly,
      matches: matchList.length,
      perDay: days ? round1(matchList.length / days) : 0,
      avgLength: Math.round(avg(lengths)),
      medianLength: Math.round(median(lengths)),
      // Completion = reached the portal; disconnection = ended by, or had, a disconnect.
      completionMatchPct: pct(matchList.filter(m => m.reports.some(r => r.er === 0)).length, matchList.length),
      completionPlayerPct: pct(reports.filter(r => r.er === 0).length, reports.length),
      disconnectMatchPct: pct(matchList.filter(m => m.reports.some(isDisconnect)).length, matchList.length),
      disconnectPlayerPct: pct(reports.filter(isDisconnect).length, reports.length),
      spikePct: pct(spiky, withSeries.length),
      bySource,
      complete: completeMatches.length,
    };
  })();

  // ---- period charts ---------------------------------------------------------
  const g1 = (() => groupByPeriod(reports, r => r.date, scales.g1, dataThrough).map(b => {
    const c = [0, 0, 0];
    b.items.forEach(r => { c[r.src]++; });
    return { label: b.label, both: c[0], s3: c[1], dynamodb: c[2] };
  }))();

  const playedByDevice = (() => playedDates(reportsAnyDate))();
  const g2 = (() => entryByPeriod(entry, playedByDevice, scales.g2, dataThrough))();

  const g7 = (() => groupByPeriod(reports, r => r.date, scales.g7, dataThrough).map(b => {
    const c = [0, 0, 0, 0];
    b.items.forEach(r => { if (r.er >= 0 && r.er < 4) c[r.er]++; });
    const n = c[0] + c[1] + c[2] + c[3];
    return { label: b.label, n, portal: pct(c[0], n), menu: pct(c[1], n), opponent: pct(c[2], n), disconnected: pct(c[3], n) };
  }))();

  const g19 = (() => groupByPeriod(reports.filter(r => r.ping > 0), r => r.date, scales.g19, dataThrough).map(b => {
    const v = b.items.map(r => r.ping);
    return { label: b.label, median: round1(median(v)), p90: round1(percentile(v, 0.9)), n: v.length, incomplete: b.incomplete };
  }))();

  const g21 = (() => groupByPeriod(matchList, m => m.date, scales.g21, dataThrough).map(b => {
    const withSeries = b.items.filter(m => m.reports.some(r => r.sn > 0));
    const spikes = withSeries.filter(m => m.reports.some(r => r.speak > 300)).length;
    return { label: b.label, spikes, withSeries: withSeries.length, pct: pct(spikes, withSeries.length), incomplete: b.incomplete };
  }))();

  const g26 = (() => {
    const regionOf = (m: MatchRow) => m.reports[0]?.region || 'unknown';
    const totals = new Map<string, number>();
    matchList.forEach(m => totals.set(regionOf(m), (totals.get(regionOf(m)) || 0) + 1));
    const top = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6).map(e => e[0]);
    const keys = totals.size > top.length ? [...top, 'other'] : top;
    const rows = groupByPeriod(matchList, m => m.date, scales.g26, dataThrough).map(b => {
      const row: Record<string, string | number> = { label: b.label };
      keys.forEach(k => { row[k] = 0; });
      b.items.forEach(m => {
        const k = top.includes(regionOf(m)) ? regionOf(m) : 'other';
        row[k] = (row[k] as number) + 1;
      });
      return row;
    });
    return { keys, rows };
  })();

  const g31 = (() => {
    const totals = new Map<string, number>();
    reports.forEach(r => totals.set(r.ver || 'unknown', (totals.get(r.ver || 'unknown') || 0) + 1));
    const keys = Array.from(totals.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const rows = groupByPeriod(reports, r => r.date, scales.g31, dataThrough).map(b => {
      const row: Record<string, string | number> = { label: b.label };
      keys.forEach(k => { row[k] = 0; });
      b.items.forEach(r => { const k = r.ver || 'unknown'; row[k] = (row[k] as number) + 1; });
      return row;
    });
    return { keys, rows };
  })();

  // ---- everything else -------------------------------------------------------
  const d = (() => {
    const playedDevs = new Set(reports.filter(r => r.dev >= 0).map(r => r.dev));

    // G3 conversion by the version a player first opened the panel on
    const byVersion = new Map<string, { opened: number; played: number }>();
    firstVersionByDevice(entry).forEach((ver, dev) => {
      const x = byVersion.get(ver) || { opened: 0, played: 0 };
      x.opened++;
      if (playedDevs.has(dev)) x.played++;
      byVersion.set(ver, x);
    });
    const g3 = Array.from(byVersion.entries())
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      .map(([version, x]) => ({ version, ...x, conversion: pct(x.played, x.opened) }));

    // G4 repeat visits
    const presses = new Map<number, number>();
    entry.forEach(e => presses.set(e.dev, (presses.get(e.dev) || 0) + e.n));
    const g4 = ['1', '2', '3', '4', '5', '6', '7', '8+'].map(bin => ({ bin, played: 0, never: 0 }));
    presses.forEach((n, dev) => {
      const row = g4[Math.min(Math.max(n, 1), 8) - 1];
      if (playedDevs.has(dev)) row.played++; else row.never++;
    });

    // G8 / G9
    const endCounts = [0, 0, 0, 0];
    reports.forEach(r => { if (r.er >= 0 && r.er < 4) endCounts[r.er]++; });
    const quitTimes = reports.filter(r => r.er === 1).map(r => r.el);

    // G10 / G11 (complete matches)
    let hostWins = 0;
    let guestWins = 0;
    const matrix = [0, 1, 2, 3].map(() => [0, 0, 0, 0]);
    completeMatches.forEach(m => {
      if (m.host.er === 0 && m.host.won) hostWins++;
      if (m.guest.er === 0 && m.guest.won) guestWins++;
      if (m.host.er >= 0 && m.host.er < 4 && m.guest.er >= 0 && m.guest.er < 4) matrix[m.host.er][m.guest.er]++;
    });
    const matrixMax = Math.max(1, ...matrix.flat());

    // G13 / G14 per map
    const byScene = new Map<string, MpReport[]>();
    reports.forEach(r => {
      const k = r.scene || 'unknown';
      const list = byScene.get(k);
      if (list) list.push(r); else byScene.set(k, [r]);
    });
    const scenes = Array.from(byScene.entries()).sort((a, b) => b[1].length - a[1].length);
    const g13 = scenes.map(([scene, rs]) => {
      const c = [0, 0, 0, 0];
      rs.forEach(r => { if (r.er >= 0 && r.er < 4) c[r.er]++; });
      const n = rs.length;
      return { scene: `${scene} (n=${n})`, n, portal: pct(c[0], n), menu: pct(c[1], n), opponent: pct(c[2], n), disconnected: pct(c[3], n) };
    });
    const g14 = scenes.map(([scene, rs]) => ({
      scene,
      deaths: round1(avg(rs.map(r => r.deaths))),
      respawns: round1(avg(rs.map(r => r.resp))),
      checkpoints: round1(avg(rs.map(r => r.cp))),
    }));

    // G15 - G17
    const readyTimes = reports.filter(r => r.ready > 0).map(r => r.ready);
    const v221 = reports.filter(r => r.ver === '2.21');
    const hostWait = v221.filter(r => r.mt === 1 && r.search > 0).map(r => r.search);
    const mmSearch = v221.filter(r => r.mt === 3 && r.search > 0).map(r => r.search);
    const g16 = [
      { name: 'Host waiting for a guest', median: round1(median(hostWait)), p90: round1(percentile(hostWait, 0.9)), n: hostWait.length },
      { name: 'Matchmaking search', median: round1(median(mmSearch)), p90: round1(percentile(mmSearch, 0.9)), n: mmSearch.length },
    ];
    const typeCounts = [0, 0, 0, 0];
    v221.forEach(r => { if (r.mt >= 1 && r.mt <= 3) typeCounts[r.mt]++; });

    // G20 ping by region
    const pings = reports.filter(r => r.ping > 0);
    const byRegion = new Map<string, number[]>();
    pings.forEach(r => {
      const k = r.region || 'unknown';
      const list = byRegion.get(k);
      if (list) list.push(r.ping); else byRegion.set(k, [r.ping]);
    });
    const g20 = Array.from(byRegion.entries())
      .map(([region, v]) => ({ region, label: `${regionLabel(region)} · n=${v.length}`, median: round1(median(v)), n: v.length }))
      .sort((a, b) => b.median - a.median);
    const overallPing = round1(median(pings.map(r => r.ping)));

    // G22 - G25
    const p95s = reports.filter(r => r.sn >= 20).map(r => r.sp95);
    const gaps = completeMatches.filter(m => m.host.ping > 0 && m.guest.ping > 0).map(m => Math.abs(m.host.ping - m.guest.ping));
    const jitters = reports.filter(r => r.jit > 0).map(r => r.jit);
    const sent = histogram(reports.map(r => r.bs / 1024), 100, 2000, ' KB');
    const recv = histogram(reports.map(r => r.br / 1024), 100, 2000, ' KB');
    const g25 = sent.map((b, i) => ({ bin: b.bin, sent: b.count, received: recv[i].count }));

    // G27 players by country (entry geo for opened, report geo for played)
    const addTo = (m: Map<string, Set<number>>, k: string, dev: number) => {
      const s = m.get(k);
      if (s) s.add(dev); else m.set(k, new Set([dev]));
    };
    const openedBy = new Map<string, Set<number>>();
    const playedBy = new Map<string, Set<number>>();
    entry.forEach(e => { if (e.geo) addTo(openedBy, e.geo, e.dev); });
    reports.forEach(r => { if (r.dev >= 0 && r.geo) addTo(playedBy, r.geo, r.dev); });
    const g36 = Array.from(new Set([...openedBy.keys(), ...playedBy.keys()]))
      .map(country => ({ country, opened: openedBy.get(country)?.size || 0, played: playedBy.get(country)?.size || 0 }))
      .sort((a, b) => b.opened - a.opened || b.played - a.played);
    const g27 = g36.slice(0, 15);

    // G29 / G30 / G32
    const platformCounts = new Map<string, number>();
    reports.forEach(r => platformCounts.set(r.plat, (platformCounts.get(r.plat) || 0) + 1));
    const inputCounts = [0, 0, 0, 0, 0];
    reports.forEach(r => { if (r.input >= 0 && r.input < inputCounts.length) inputCounts[r.input]++; });
    const abilityNames = snapshot?.enums.abilities ?? [];
    const abilityTotals = abilityNames.map(() => 0);
    reports.forEach(r => (r.ab || []).forEach((n, i) => { if (i < abilityTotals.length) abilityTotals[i] += n || 0; }));
    const g32 = abilityNames
      .map((name, i) => ({ name, perMatch: reports.length ? round1(abilityTotals[i] / reports.length) : 0 }))
      .filter(x => x.perMatch > 0)
      .sort((a, b) => b.perMatch - a.perMatch);

    return {
      g3, g4, endCounts, quitTimes, hostWins, guestWins, matrix, matrixMax, g13, g14,
      readyTimes, g16, typeCounts, g20, overallPing, p95s, gaps, jitters, g25, g27, g36,
      platformCounts, inputCounts, g32,
    };
  })();

  return {
    reports, reportsAnyDate, entry, matchList, completeMatches, overview,
    g1, g2, g7, g19, g21, g26, g31, d, windowLabel: bounds.label,
  };
}

type WindowData = ReturnType<typeof computeWindowData>;

export function MultiplayerDashboard({ isAdmin }: { isAdmin: boolean }) {
  const t = useChartTheme();
  const isDark = t.isDark;
  const { snapshot, unavailableMessage, status, isLoading, error, isBusy, isQueueing, syncMessage, queueSync, reload } =
    useMultiplayerAnalytics();
  const pvp = usePvpLevels(!!snapshot?.pvpLevels?.available, snapshot?.generatedAt ?? null);

  const [filters, setFilters] = useState<MpFilters>({ platform: ALL, version: ALL, region: ALL, scene: ALL, mode: ALL, countries: [] });
  // Match, matchmaking, network and geography data only exist for the online modes.
  const onlineView = filters.mode === ALL || ONLINE_MODES.has(filters.mode);
  const [scales, setScales] = useState<Record<ScaleKey, TimeScale>>({
    g1: 'weekly', g2: 'weekly', g7: 'weekly', g19: 'weekly', g21: 'weekly', g26: 'weekly', g31: 'weekly',
  });
  const scaleToggle = (k: ScaleKey) => (
    <TimeScaleToggle scale={scales[k]} onChange={s => setScales(p => ({ ...p, [k]: s }))} isDark={isDark} />
  );

  const dataThrough = snapshot?.dataThrough ?? '';

  // ---- decode + filter -------------------------------------------------------
  const allReports = useMemo(() => (snapshot ? decodeReports(snapshot) : []), [snapshot]);
  // Invite-only players count as having opened multiplayer (on the days they played).
  const allEntry = useMemo(() => (snapshot ? withInviteEntries(decodeEntry(snapshot), allReports) : []), [snapshot, allReports]);
  const options = useMemo(() => ({
    platforms: distinct([...allReports.map(r => r.plat), ...allEntry.map(e => e.plat), ...(pvp.data?.platforms ?? [])]),
    versions: distinct([...allReports.map(r => r.ver), ...allEntry.map(e => e.ver)]),
    regions: distinct(allReports.map(r => r.region)),
    // Every scene ever reported (all time) plus the PVP Local / Editor levels, limited to the selected mode.
    levels: distinct([
      ...(snapshot?.scenes ?? []).filter(s => filters.mode === ALL
        || filters.mode === (s === '13_LABS_3D_COOP_ONLINE_LEVELEDITOR' ? 'editor_online' : 'online')),
      ...allReports.filter(r => filters.mode === ALL || r.mode === filters.mode).map(r => r.scene),
      ...(pvp.data?.levels ?? []).filter(l => filters.mode === ALL || (pvp.data?.levelModes[l] ?? 'local') === filters.mode),
    ]),
  }), [allReports, allEntry, snapshot, pvp.data, filters.mode]);

  // Country options: distinct players per country (menu entries + match reports).
  // 'Unknown' counts S3-only match reports, which carry no country.
  const countryOpts = useMemo<CountryOption[]>(() => {
    const devs = new Map<string, Set<number>>();
    const add = (geo: string, dev: number) => {
      const cc = (geo || '').toUpperCase();
      if (!cc) return;
      const s = devs.get(cc);
      if (s) s.add(dev); else devs.set(cc, new Set([dev]));
    };
    allEntry.forEach(e => add(e.geo, e.dev));
    allReports.forEach(r => { if (r.dev >= 0) add(r.geo, r.dev); });
    const opts = Array.from(devs.entries()).map(([code, s]) => ({ code, name: countryName(code), count: s.size }));
    const noCountry = allReports.filter(r => !r.geo).length;
    if (noCountry) opts.push({ code: UNKNOWN_COUNTRY, name: 'Unknown (S3-only reports)', count: noCountry });
    return opts;
  }, [allEntry, allReports]);

  // Every chart has its own window: 'range' follows the date range; Day / Week / Month / All Time override it.
  const [windows, setWindows] = useState<Record<string, ChartWindow>>({});
  const { inRange: dateInRange, start: dateStart, end: dateEnd } = useDateRange();
  const cardOverrides = useCardOverrides();
  // One view per (window, countries); computed only when a card asks for it.
  const viewCache = useMemo(
    () => new Map<string, WindowData>(),
    [allReports, allEntry, filters, scales, dataThrough, snapshot, dateInRange, dateStart, dateEnd]
  );
  const viewOf = (win: ChartWindow, countries: string[]): WindowData => {
    const key = `${win}|${[...countries].sort().join(',')}`;
    let v = viewCache.get(key);
    if (!v) {
      v = computeWindowData(win, allReports, allEntry, { ...filters, countries }, scales, dataThrough, snapshot,
        dateInRange, dateStart, dateEnd);
      viewCache.set(key, v);
    }
    return v;
  };
  const { reports, matchList, overview } = viewOf('range', filters.countries);

  // ---- historical funnel (fixed window, ignores filters) ---------------------
  const funnel = snapshot?.funnelV221 ?? null;
  const g5 = useMemo(() => {
    if (!funnel) return [];
    const base = funnel.playersWithReports || 1;
    return [
      { label: 'Opened the Online PVP panel', players: funnel.opened, color: '#3b82f6', note: '' },
      { label: 'Started an online session', players: funnel.startedSession, color: '#3b82f6', note: `${pct(funnel.startedSession, funnel.opened)}% of opened` },
      ...funnel.steps.map((s, i) => ({
        label: STEP_LABELS[s.step] || s.step, players: s.players, color: STEP_COLORS[i] || '#64748b',
        note: `${pct(s.players, base)}% of players with a session`,
      })),
    ];
  }, [funnel]);
  const g6 = useMemo(() => {
    if (!funnel) return [];
    const steps = snapshot?.enums.funnelSteps ?? [];
    return Object.entries(funnel.byMatchType).map(([type, counts]) => {
      const n = counts.reduce((a, b) => a + b, 0);
      const row: Record<string, string | number> = { type: `${type.charAt(0).toUpperCase()}${type.slice(1)} (n=${n})` };
      steps.forEach((s, i) => { row[s] = pct(counts[i] || 0, n); });
      return row;
    });
  }, [funnel, snapshot]);

  // ---- render helpers ----------------------------------------------------------
  const selectCls = `rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark ? 'bg-gray-800 border border-gray-700 text-gray-100' : 'bg-white border border-gray-300 text-gray-900'
  }`;
  const filterSelect = (key: keyof MpFilters, label: string, values: string[], format?: (v: string) => string) => (
    <label className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
      {label}
      <select className={selectCls} value={filters[key]} onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}>
        <option value={ALL}>All</option>
        {values.map(v => <option key={v} value={v}>{format ? format(v) : v}</option>)}
      </select>
    </label>
  );
  const grid = <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />;
  const yAxis = (props: Record<string, unknown> = {}) => <YAxis tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} {...props} />;
  const tooltip = (props: Record<string, unknown> = {}) => <Tooltip contentStyle={t.tooltip} {...props} />;
  const legend = <Legend wrapperStyle={{ fontSize: 11 }} />;
  const completeValues = <T extends { incomplete: boolean }>(rows: T[], get: (r: T) => number) =>
    rows.filter(r => !r.incomplete).map(get);
  const W = (id: string, render: (x: WindowData) => ReactNode) => {
    const w = windows[id] ?? 'range';
    return (
      <CountryCard id={id}>{selection => {
        const x = viewOf(w, selection);
        return (
          <ChartWindowContext.Provider value={{ value: w, onChange: v => setWindows(p => ({ ...p, [id]: v })), label: x.windowLabel }}>
            {render(x)}
          </ChartWindowContext.Provider>
        );
      }}</CountryCard>
    );
  };

  // ---- states --------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className={`h-8 w-8 animate-spin mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading Multiplayer Analytics...</p>
        </div>
      </div>
    );
  }

  const syncControls = isAdmin && (
    <div className="flex items-center gap-2">
      <button onClick={() => queueSync(false)} disabled={isBusy || isQueueing}
        title="Queue an incremental sync for the analytics worker"
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50">
        <RefreshCw className={`h-4 w-4 ${isBusy ? 'animate-spin' : ''}`} />Sync now
      </button>
      <button
        onClick={() => {
          if (window.confirm('Full rebuild re-downloads every S3 match file and rebuilds the data. The current data keeps showing until it succeeds. Continue?')) {
            queueSync(true);
          }
        }}
        disabled={isBusy || isQueueing}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
          isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}>
        <RotateCw className="h-4 w-4" />Full rebuild
      </button>
    </div>
  );

  if (!snapshot) {
    return (
      <div className="p-6">
        <ThemedCard isDark={isDark}>
          <div className="p-8 text-center space-y-3">
            <AlertTriangle className={`h-12 w-12 mx-auto ${error ? 'text-red-500' : isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {error ? 'Failed to load Multiplayer data' : 'No Multiplayer data yet'}
            </h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} max-w-lg mx-auto`}>{error || unavailableMessage}</p>
            {status && <StatusLine status={status} isDark={isDark} />}
            <div className="flex justify-center gap-2">
              <button onClick={() => reload()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Retry</button>
              {syncControls}
            </div>
            {syncMessage && <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{syncMessage}</p>}
          </div>
        </ThemedCard>
      </div>
    );
  }

  const u = snapshot.counts.union;
  const showWarning = status && (status.state === 'failed' || status.state === 'kept_previous' || status.stale);
  const anyFilter = filters.platform !== ALL || filters.version !== ALL || filters.region !== ALL
    || filters.scene !== ALL || filters.mode !== ALL || filters.countries.length > 0;

  return (
    <CountryScope value={{
      tabSelection: filters.countries, options: countryOpts, isDark,
      countLabel: 'players (Unknown = S3-only match reports)', ...cardOverrides,
    }}>
    <>
      {/* Floating date-range card, same as the Analytics and Sony/DOMO tabs. */}
      <div className="hidden xl:block fixed left-4 top-[7.5rem] z-40">
        <div className={`w-48 rounded-xl border shadow-lg backdrop-blur-sm p-3 ${isDark ? 'bg-gray-900/90 border-gray-700' : 'bg-white/90 border-gray-200'}`}>
          <div className={`flex items-center gap-1.5 mb-2.5 text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <CalendarDays className="h-3.5 w-3.5" />
            Date Range
          </div>
          <DateRangeFilter orientation="vertical" />
          <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Globe className="h-3.5 w-3.5" />
              Country/Region
            </div>
            <CountryMultiSelect options={countryOpts} selected={filters.countries} isDark={isDark} compact align="left"
              onChange={codes => setFilters(f => ({ ...f, countries: codes }))}
              countLabel="players (Unknown = S3-only match reports)" />
            <div className="mt-3">
              <CountryChips options={countryOpts} selected={filters.countries} isDark={isDark} vertical max={8}
                onChange={codes => setFilters(f => ({ ...f, countries: codes }))} />
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4 xl:pl-56">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 rounded-lg"><Swords className="h-6 w-6 text-white" /></div>
            <div>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Multiplayer Analytics</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Online PVP and PVP levels · data through {snapshot.dataThrough}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="xl:hidden"><DateRangeFilter /></div>
            {status && (
              <div className={`flex flex-col px-3 py-1.5 rounded-lg text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                <div><span className="font-medium">Last run:</span> {STATE_TEXT[status.state] || status.state} · {formatWhen(status.finishedAt || status.startedAt)}</div>
                <div><span className="font-medium">Next run:</span> {formatWhen(status.nextScheduledRun)}{status.queued ? ' · sync queued' : ''}</div>
              </div>
            )}
            {syncControls}
          </div>
        </div>

        {/* ============ FILTERS (for all data) ============ */}
        <div className="flex items-center gap-3 flex-wrap">
          {filterSelect('mode', 'Mode', [...PVP_MODE_ORDER], v => PVP_MODE_LABELS[v] || v)}
          {filterSelect('platform', 'Platform', options.platforms)}
          {filterSelect('scene', 'Level/Map', options.levels)}
          {/* Below xl the floating card is hidden: same tab filter inline. */}
          <div className="xl:hidden">
            <CountryMultiSelect options={countryOpts} selected={filters.countries} isDark={isDark}
              onChange={codes => setFilters(f => ({ ...f, countries: codes }))}
              countLabel="players (Unknown = S3-only match reports)" align="left" />
          </div>
          {SHOW_VERSION_REGION_FILTERS && filterSelect('version', 'Version', options.versions)}
          {SHOW_VERSION_REGION_FILTERS && filterSelect('region', 'Server region', options.regions, regionLabel)}
          {anyFilter && (
            <button onClick={() => setFilters({ platform: ALL, version: ALL, region: ALL, scene: ALL, mode: ALL, countries: [] })}
              className={`text-xs underline ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Clear filters</button>
          )}
        </div>

        {syncMessage && <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{syncMessage}</p>}
        {showWarning && (
          <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${isDark ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              {status?.stale ? 'The last run stopped reporting progress — the analytics worker may have restarted.' : `Last run: ${STATE_TEXT[status!.state]}.`}
              {status?.error && <span className="ml-1">{status.error}</span>}
              <span className="ml-1">The data below is from {formatWhen(snapshot.generatedAt)}.</span>
            </div>
          </div>
        )}

        {/* ============ TOP HIGHLIGHT BAR ============ */}
        {onlineView && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <StatTile label="Total matches" value={fmtInt(overview.matches)} sub={`${overview.perDay}/day`} tone="orange" />
          <StatTile label="Average match length" value={`${overview.avgLength} s`} sub={`median ${overview.medianLength} s`} />
          <StatTile label="Completion rate · per match" value={`${overview.completionMatchPct}%`} sub="a player reached the portal" tone="green" />
          <StatTile label="Completion rate · per player" value={`${overview.completionPlayerPct}%`} sub="player reports that reached the portal" tone="green" />
          <StatTile label="Disconnection rate · per match" value={`${overview.disconnectMatchPct}%`} sub="either player disconnected" tone="red" />
          <StatTile label="Disconnection rate · per player" value={`${overview.disconnectPlayerPct}%`} sub="player reports with a disconnect" tone="red" />
        </div>
        )}

        {/* ============ LAYOUT PASS 2: graphs 2 per row, other elements 3 per row ============ */}

        {/* ============ PLAYERS BY COUNTRY ============ */}
        {onlineView && W('G36', ({ d }) => <PlayersByCountryCard rows={d.g36} />)}

        {/* ============ PVP: PLAYERS BY SESSION TIME (all modes) ============ */}
        <CountryCard id="g33">{selection => (
        <PvpLevelsSection data={pvp.data} loading={pvp.loading} error={pvp.error}
          dataThrough={snapshot.dataThrough} platform={filters.platform} scene={filters.scene}
          mode={filters.mode} countries={selection} reports={viewOf('range', selection).reportsAnyDate} />
        )}</CountryCard>

        {!onlineView && (
          <ThemedCard isDark={isDark}>
            <div className={`p-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {PVP_MODE_LABELS[filters.mode]} comes from analytics level events, which have no matches, ping or
              network data. The highlight bar, matchmaking, network and geography graphs cover PVP Online and
              PVP Editor Online — pick one of those modes (or All) to see them.
            </div>
          </ThemedCard>
        )}

        {onlineView && (<>
        {/* ============ MATCHES & MATCHMAKING ============ */}
        <SectionTitle title="Matches & matchmaking" subtitle="Online PVP matches in the filters" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {W('G2', ({ overview, g2 }) => (
          <ChartCard id="G2" title="Player funnel · opened → played" badges={['lowerBound', 'noRegion']} n={overview.opened} right={scaleToggle('g2')}
            footer="Opened = used the main-menu button, or joined by invite (counted on the days they played). Played = played a match between the start of the period and 14 days after it ended. * = still filling up.">
            {g2.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={g2} margin={MARKER_CHART_MARGIN}>
                  {grid}
                  <XAxis {...periodAxis(t)} />
                  {yAxis({ yAxisId: 'left', allowDecimals: false })}
                  {yAxis({ yAxisId: 'right', orientation: 'right', unit: '%' })}
                  {tooltip()}
                  {legend}
                  <Line yAxisId="left" type="monotone" dataKey="opened" name="Opened" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="left" type="monotone" dataKey="played" name="Played" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  {athNowLines(completeValues(g2, r => r.conversion), v => `${v}%`, 'right')}
                  <Line yAxisId="right" type="monotone" dataKey="conversion" name="Conversion %" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G7', ({ reports, g7 }) => (
          <ChartCard id="G7" title="Match results over time" n={reports.length} right={scaleToggle('g7')}>
            {g7.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={g7}>
                  {grid}
                  <XAxis {...periodAxis(t)} />
                  {yAxis({ domain: [0, 100], allowDataOverflow: true, ticks: [0, 25, 50, 75, 100], unit: '%' })}
                  {tooltip({ formatter: (v: number, name: string) => [`${v}%`, name] })}
                  {legend}
                  {END_KEYS.map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} name={END_REASON_LABELS[i]} stroke={END_REASON_COLORS[i]} strokeWidth={2} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G8', ({ reports, d }) => (
          <ChartCard id="G8" title="Match results" n={reports.length}>
            <Donut data={END_REASON_LABELS.map((name, i) => ({ name, value: d.endCounts[i], color: END_REASON_COLORS[i] }))} />
          </ChartCard>
          ))}
          {W('G13', ({ reports, d }) => (
          <ChartCard id="G13" title="Match results per map" n={reports.length} height={Math.max(220, 50 + d.g13.length * 26)}>
            {d.g13.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.g13} layout="vertical" margin={{ left: 10, right: 20 }}>
                  {grid}
                  <XAxis type="number" domain={[0, 100]} allowDataOverflow unit="%" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  <YAxis type="category" dataKey="scene" width={170} tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {tooltip({ formatter: (v: number, name: string) => [`${v}%`, name] })}
                  {legend}
                  {END_KEYS.map((k, i) => (
                    <Bar key={k} dataKey={k} name={END_REASON_LABELS[i]} fill={END_REASON_COLORS[i]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G17', ({ d }) => (
          <ChartCard id="G17" title="Matchmaking · Host vs Join vs Matchmaking" badges={['v221']} n={d.typeCounts[1] + d.typeCounts[2] + d.typeCounts[3]}
            footer="Only v2.21 recorded how players joined.">
            <Donut data={[1, 2, 3].map(i => ({ name: MATCH_TYPE_LABELS[i], value: d.typeCounts[i], color: PALETTE[i - 1] }))} />
          </ChartCard>
          ))}
          {W('G16', ({ d }) => (
          <ChartCard id="G16" title="Matchmaking · wait / search times" badges={['v221']} n={d.g16[0].n + d.g16[1].n}
            footer="Only v2.21 recorded wait and search time.">
            {d.g16.every(r => !r.n) ? <EmptyChart message="No v2.21 matches in the filters" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.g16}>
                  {grid}
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: t.text }} stroke={t.grid} />
                  {yAxis({ unit: ' s' })}
                  {tooltip({ formatter: (v: number, name: string) => [`${v} s`, name] })}
                  {legend}
                  <Bar dataKey="median" name="Median" fill="#3b82f6" />
                  <Bar dataKey="p90" name="p90" fill="#a855f7" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
        </div>

        {/* ============ NETWORK ============ */}
        <SectionTitle title="Network" subtitle="Ping, lag spikes and data use" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {W('G19', ({ g19 }) => (
          <ChartCard id="G19" title="Ping over time" n={g19.reduce((s, r) => s + r.n, 0)} right={scaleToggle('g19')}
            footer="Average ping per player report.">
            {g19.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={g19} margin={MARKER_CHART_MARGIN}>
                  {grid}
                  <XAxis {...periodAxis(t)} />
                  {yAxis({ unit: ' ms' })}
                  {tooltip({ formatter: (v: number, name: string) => [`${v} ms`, name] })}
                  {legend}
                  {athNowLines(completeValues(g19, r => r.median), v => `${v} ms`)}
                  <Line type="monotone" dataKey="median" name="Median" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="p90" name="p90" stroke="#a855f7" strokeWidth={2} dot={{ r: 2 }} strokeDasharray="5 3" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G21', ({ g21 }) => (
          <ChartCard id="G21" title="Lag spikes over time" n={g21.reduce((s, r) => s + r.withSeries, 0)} right={scaleToggle('g21')}
            footer="Matches whose per-second ping peaked above 300 ms (matches with per-second data only).">
            {g21.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={g21} margin={MARKER_CHART_MARGIN}>
                  {grid}
                  <XAxis {...periodAxis(t)} />
                  {yAxis({ yAxisId: 'left', allowDecimals: false })}
                  {yAxis({ yAxisId: 'right', orientation: 'right', unit: '%' })}
                  {tooltip()}
                  {legend}
                  <Line yAxisId="left" type="monotone" dataKey="spikes" name="Matches with a spike" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  {athNowLines(completeValues(g21, r => r.pct), v => `${v}%`, 'right')}
                  <Line yAxisId="right" type="monotone" dataKey="pct" name="% of matches" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G20', ({ d }) => (
          <ChartCard id="G20" title="Ping by server region" n={d.g20.reduce((s, r) => s + r.n, 0)} height={Math.max(230, 40 + d.g20.length * 22)}
            footer={`Dashed line: overall median ${d.overallPing} ms. Grey bars have fewer than 10 reports.`}>
            {d.g20.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.g20} layout="vertical" margin={{ left: 0, right: 36 }} barCategoryGap="20%">
                  {grid}
                  <XAxis type="number" unit=" ms" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  <YAxis type="category" dataKey="label" width={200} interval={0} tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {tooltip({ formatter: (v: number) => [`${v} ms`, 'Median ping'] })}
                  <ReferenceLine x={d.overallPing} stroke="#eab308" strokeDasharray="4 3" />
                  <Bar dataKey="median">
                    {d.g20.map(r => <Cell key={r.region} fill={r.n < 10 ? '#9ca3af' : '#3b82f6'} />)}
                    <LabelList dataKey="median" position="right" fill={t.text} fontSize={10} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G25', ({ reports, d }) => (
          <ChartCard id="G25" title="Data used per match" n={reports.length}>
            {reports.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.g25}>
                  {grid}
                  <XAxis dataKey="bin" tick={{ fontSize: 9, fill: t.text }} stroke={t.grid} angle={-35} textAnchor="end" height={55} interval="preserveStartEnd" />
                  {yAxis({ allowDecimals: false })}
                  {tooltip()}
                  {legend}
                  <Line type="monotone" dataKey="sent" name="Sent" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="received" name="Received" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
        </div>

        {/* ============ GEOGRAPHY & PLATFORMS ============ */}
        <SectionTitle title="Geography & platforms" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {W('G27', ({ overview, d }) => (
          <ChartCard id="G27" title="Players by country (top 15)" badges={['lowerBound', 'noRegion']} n={overview.opened} height={Math.max(260, 50 + d.g27.length * 17)}>
            {d.g27.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.g27} layout="vertical" margin={{ left: 10, right: 30 }}>
                  {grid}
                  <XAxis type="number" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} allowDecimals={false} />
                  <YAxis type="category" dataKey="country" width={40} tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {tooltip()}
                  {legend}
                  <Bar dataKey="opened" name="Opened" fill="#94a3b8" />
                  <Bar dataKey="played" name="Played" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G26', ({ matchList, g26 }) => (
          <ChartCard id="G26" title="Matches by server region" n={matchList.length} right={scaleToggle('g26')}>
            {g26.rows.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={g26.rows}>
                  {grid}
                  <XAxis {...periodAxis(t)} />
                  {yAxis({ allowDecimals: false })}
                  {tooltip()}
                  {legend}
                  {g26.keys.map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} name={k === 'other' ? 'Other' : regionLabel(k)} stroke={k === 'other' ? '#64748b' : PALETTE[i]} strokeWidth={2} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G29', ({ reports, d }) => (
          <ChartCard id="G29" title="Platform distribution" n={reports.length}>
            <Donut data={Array.from(d.platformCounts.entries()).sort((a, b) => b[1] - a[1])
              .map(([name, value], i) => ({ name, value, color: PALETTE[i % PALETTE.length] }))} />
          </ChartCard>
          ))}
          {W('G30', ({ reports, d }) => (
          <ChartCard id="G30" title="Input distribution" n={reports.length}>
            <Donut data={INPUT_LABELS.map((name, i) => ({ name, value: d.inputCounts[i] || 0, color: PALETTE[(i + 3) % PALETTE.length] }))} />
          </ChartCard>
          ))}
        </div>

        {/* ============ MORE DETAIL (outside the main layout, kept) ============ */}
        <SectionTitle title="More detail" subtitle="Additional graphs outside the main layout" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <StatTile label="Opened Online PVP" value={fmtInt(overview.opened)} sub="players · menu + invites" />
          <StatTile label="Played a match" value={fmtInt(overview.played)} sub="players · lower bound" tone="green" />
          <StatTile label="Opened → played" value={`${overview.conversion}%`} sub="of players who opened" tone="purple" />
          <StatTile label="Matches with a lag spike" value={`${overview.spikePct}%`} sub="peak ping > 300 ms" tone="red" />
          <StatTile label="Joined only by invite" value={fmtInt(overview.inviteOnly)} sub="players · included in opened" tone="gray" />
          <StatTile label="Reports by source" value={fmtInt(reports.length)}
            sub={`both ${fmtInt(overview.bySource[0])} · S3 ${fmtInt(overview.bySource[1])} · DynamoDB ${fmtInt(overview.bySource[2])}`} tone="gray" />
          <StatTile label="Data through" value={snapshot.dataThrough} sub={`built ${formatWhen(snapshot.generatedAt)}`} tone="gray" />
          <StatTile label="All-time union" value={fmtInt(u.matches)} sub={`matches · ${fmtInt(u.reports)} reports · ${fmtInt(u.conflicts)} conflicts`} tone="gray" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {W('G1', ({ reports, g1 }) => (
          <ChartCard id="G1" title="Where match data came from" n={reports.length} right={scaleToggle('g1')}>
            {g1.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={g1}>
                  {grid}
                  <XAxis {...periodAxis(t)} />
                  {yAxis({ allowDecimals: false })}
                  {tooltip()}
                  {legend}
                  {(['both', 's3', 'dynamodb'] as const).map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} name={SOURCE_LABELS[i]} stroke={SOURCE_COLORS[i]} strokeWidth={2} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G31', ({ reports, g31 }) => (
          <ChartCard id="G31" title="Client versions over time" n={reports.length} right={scaleToggle('g31')}>
            {g31.rows.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={g31.rows}>
                  {grid}
                  <XAxis {...periodAxis(t)} />
                  {yAxis({ allowDecimals: false })}
                  {tooltip()}
                  {legend}
                  {g31.keys.map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G3', ({ overview, d }) => (
          <ChartCard id="G3" title="Conversion by client version" badges={['lowerBound', 'noRegion']} n={overview.opened}
            footer="Players grouped by the version they first opened the panel on (in the selected range).">
            {d.g3.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.g3}>
                  {grid}
                  <XAxis dataKey="version" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {yAxis({ allowDecimals: false })}
                  {tooltip({ formatter: (v: number, name: string) => [v.toLocaleString(), name] })}
                  {legend}
                  <Line type="monotone" dataKey="opened" name="Opened" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="played" name="Played" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="conversion" position="top" formatter={(v: number) => `${v}%`} fill={t.text} fontSize={10} />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G4', ({ overview, d }) => (
          <ChartCard id="G4" title="Repeat visits" badges={['lowerBound', 'noRegion']} n={overview.opened}
            footer={`Times each player opened the panel in the range (an invite join counts once per day). ${fmtInt(overview.inviteOnly)} players joined only by invite.`}>
            {d.g4.every(r => !r.played && !r.never) ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.g4}>
                  {grid}
                  <XAxis dataKey="bin" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid}
                    label={{ value: 'times opened', position: 'insideBottomRight', offset: -2, fill: t.text, fontSize: 10 }} />
                  {yAxis({ allowDecimals: false })}
                  {tooltip()}
                  {legend}
                  <Line type="monotone" dataKey="never" name="Never played" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="played" name="Played" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G9', ({ d }) => (
          <ChartCard id="G9" title="When players quit mid-match" n={d.quitTimes.length} footer="Seconds into the match when a player quit to the menu.">
            <HistogramChart bins={histogram(d.quitTimes, 10, 300, 's')} color="#f97316"
              medianValue={median(d.quitTimes)} medianLabel={`median ${Math.round(median(d.quitTimes))} s`} valueLabel="Reports" />
          </ChartCard>
          ))}
          {W('G10', ({ completeMatches, d }) => (
          <ChartCard id="G10" title="Host vs guest wins" badges={['complete']} n={completeMatches.length}
            footer="Portal finishes won by each side.">
            {d.hostWins + d.guestWins === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { side: 'Host', wins: d.hostWins, share: pct(d.hostWins, d.hostWins + d.guestWins) },
                  { side: 'Guest', wins: d.guestWins, share: pct(d.guestWins, d.hostWins + d.guestWins) },
                ]}>
                  {grid}
                  <XAxis dataKey="side" tick={{ fontSize: 11, fill: t.text }} stroke={t.grid} />
                  {yAxis({ allowDecimals: false })}
                  {tooltip()}
                  <Bar dataKey="wins" name="Wins">
                    <Cell fill="#3b82f6" />
                    <Cell fill="#f97316" />
                    <LabelList dataKey="share" position="top" formatter={(v: number) => `${v}%`} fill={t.text} fontSize={11} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G14', ({ reports, d }) => (
          <ChartCard id="G14" title="Deaths, respawns, checkpoints per map" n={reports.length}
            footer="Average per player report.">
            {d.g14.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.g14}>
                  {grid}
                  <XAxis dataKey="scene" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {yAxis()}
                  {tooltip()}
                  {legend}
                  <Bar dataKey="deaths" name="Deaths" fill="#ef4444" />
                  <Bar dataKey="respawns" name="Respawns" fill="#f97316" />
                  <Bar dataKey="checkpoints" name="Checkpoints" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G15', ({ d }) => (
          <ChartCard id="G15" title="Ready-up time" n={d.readyTimes.length}>
            <HistogramChart bins={histogram(d.readyTimes, 1, 30, 's')} color="#6366f1"
              medianValue={median(d.readyTimes)} medianLabel={`median ${round1(median(d.readyTimes))} s`} valueLabel="Reports" />
          </ChartCard>
          ))}
          {W('G32', ({ reports, d }) => (
          <ChartCard id="G32" title="Ability use per report" n={reports.length} footer="Abilities never used are hidden.">
            {d.g32.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.g32} layout="vertical" margin={{ left: 10, right: 40 }}>
                  {grid}
                  <XAxis type="number" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {tooltip({ formatter: (v: number) => [v, 'Uses per report'] })}
                  <Bar dataKey="perMatch" fill="#8b5cf6">
                    <LabelList dataKey="perMatch" position="right" fill={t.text} fontSize={10} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          ))}
          {W('G22', ({ d }) => (
          <ChartCard id="G22" title="Worst moments per match" n={d.p95s.length} footer="p95 of per-second ping for each report (20+ seconds of data).">
            <HistogramChart bins={histogram(d.p95s, 25, 500, ' ms')} color="#ef4444"
              medianValue={median(d.p95s)} medianLabel={`median ${Math.round(median(d.p95s))} ms`} valueLabel="Reports" />
          </ChartCard>
          ))}
          {W('G23', ({ d }) => (
          <ChartCard id="G23" title="Ping gap between players" badges={['complete']} n={d.gaps.length}>
            <HistogramChart bins={histogram(d.gaps, 10, 200, ' ms')} color="#a855f7"
              medianValue={median(d.gaps)} medianLabel={`median ${Math.round(median(d.gaps))} ms`} valueLabel="Matches" />
          </ChartCard>
          ))}
          {W('G24', ({ d }) => (
          <ChartCard id="G24" title="Jitter" n={d.jitters.length}>
            <HistogramChart bins={histogram(d.jitters, 2, 50, ' ms')} color="#06b6d4"
              medianValue={median(d.jitters)} medianLabel={`median ${round1(median(d.jitters))} ms`} valueLabel="Reports" />
          </ChartCard>
          ))}
          <ChartCard id="G5" title="Where players dropped out" badges={['v221', 'fixedWindow']} n={funnel?.opened} height={280}
            footer={funnel ? `Window ${funnel.window.from} – ${funnel.window.to}. The last six rows split the ${fmtInt(funnel.playersWithReports)} players with a session by the furthest point they reached (across all later versions).` : undefined}>
            {g5.length === 0 ? <EmptyChart message="No v2.21 data" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={g5} layout="vertical" margin={{ left: 10, right: 50 }}>
                  {grid}
                  <XAxis type="number" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={230} tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {tooltip({ formatter: (v: number, _n: string, item: { payload?: { note?: string } }) => [`${v.toLocaleString()}${item?.payload?.note ? ` · ${item.payload.note}` : ''}`, 'Players'] })}
                  <Bar dataKey="players">
                    {g5.map(r => <Cell key={r.label} fill={r.color} />)}
                    <LabelList dataKey="players" position="right" fill={t.text} fontSize={10} formatter={(v: number) => v.toLocaleString()} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          <ChartCard id="G6" title="Drop-out by entry path" badges={['v221', 'fixedWindow']} n={funnel?.reportsV221} height={280}
            footer="Share of v2.21 attempts by the furthest point reached.">
            {g6.length === 0 ? <EmptyChart message="No v2.21 data" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={g6} layout="vertical" margin={{ left: 10, right: 20 }}>
                  {grid}
                  <XAxis type="number" domain={[0, 100]} allowDataOverflow unit="%" tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  <YAxis type="category" dataKey="type" width={130} tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} />
                  {tooltip({ formatter: (v: number, name: string) => [`${v}%`, STEP_LABELS[name] || name] })}
                  <Legend wrapperStyle={{ fontSize: 10 }} formatter={(v: string) => STEP_LABELS[v] || v} />
                  {(snapshot.enums.funnelSteps || []).map((s, i) => (
                    <Bar key={s} dataKey={s} fill={STEP_COLORS[i] || PALETTE[i]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          {W('G11', ({ completeMatches, d }) => (
          <ChartCard id="G11" title="End reasons within the same match" className="lg:col-span-2" badges={['complete']} n={completeMatches.length} height={240}
            footer="Rows: how the host's report ended. Columns: how the guest's report ended.">
            {completeMatches.length === 0 ? <EmptyChart /> : (
              <div className="overflow-x-auto h-full">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className={`px-2 py-1 text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Host ↓ / Guest →</th>
                      {END_REASON_LABELS.map(l => <th key={l} className={`px-2 py-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{l}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {d.matrix.map((row, i) => (
                      <tr key={i}>
                        <td className={`px-2 py-2 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{END_REASON_LABELS[i]}</td>
                        {row.map((v, j) => (
                          <td key={j} className="px-2 py-2 text-center rounded"
                            style={{ backgroundColor: v ? `rgba(59,130,246,${0.12 + 0.7 * (v / d.matrixMax)})` : 'transparent', color: v / d.matrixMax > 0.5 ? '#fff' : t.text }}>
                            {v || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
          ))}
        </div>

        {/* ============ MATCH EXPLORER (admin, hidden for now) ============ */}
        {SHOW_MATCH_EXPLORER && isAdmin && (
          <>
            <SectionTitle title="Match explorer" subtitle="Admin only · matches in the current filters" />
            <MatchExplorer matches={matchList} />
          </>
        )}
        </>)}

        {/* ============ ABOUT THE DATA ============ */}
        <ThemedCard isDark={isDark}>
          <details className="p-4 group">
            <summary className={`cursor-pointer text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              <Database className="h-4 w-4" />About this data
            </summary>
            <ul className={`mt-2 list-disc pl-5 text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {snapshot.notes.map(n => <li key={n}>{n}</li>)}
              <li>PVP levels: a session is one round (level_exited, level_restarted or level_completed with its level_time); the PVP Editor uses its session_time.</li>
              <li>
                Sources this build: {fmtInt(snapshot.counts.s3.storeRows)} S3 files ({fmtInt(u.s3Editor)} editor tests dropped),{' '}
                {fmtInt(snapshot.counts.dynamodb.events)} DynamoDB match events, {fmtInt(snapshot.counts.entry.presses)} Online PVP presses
                {snapshot.counts.entry.excludedCountry > 0 && <> ({fmtInt(snapshot.counts.entry.excludedCountry)} from excluded countries)</>}.
              </li>
            </ul>
          </details>
        </ThemedCard>
      </div>
    </>
    </CountryScope>
  );
}

function StatusLine({ status, isDark }: { status: MpStatus; isDark: boolean }) {
  return (
    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
      Last run: {STATE_TEXT[status.state] || status.state}
      {status.finishedAt ? ` · ${formatWhen(status.finishedAt)}` : ''}
      {status.error ? ` · ${status.error}` : ''}
      {status.queued ? ' · sync queued' : ''} · next run {formatWhen(status.nextScheduledRun)}
    </p>
  );
}
